import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filter, last } from 'lodash';
import moment from 'moment';
import { statusByName, typeByName } from '../constants/Filters';
import { useFirestore } from 'react-redux-firebase';
import collections from '../constants/FirestoreCollections';
import { actions } from '../actions';
import { getDayRange, keywordType } from '../utils/utils';
import { useMySupportedChats } from './useMySupportedChats';

// sort chats by last message creation date
function sortChats(chats) {
  return chats.sort((itemA, itemB) => {
    const dateA =
      itemA.messages.length > 0 ? last(itemA.messages).creationDate.toDate().getTime() : moment();
    const dateB =
      itemB.messages.length > 0 ? last(itemB.messages).creationDate.toDate().getTime() : moment();
    return dateB - dateA;
  });
}

function filterChatsLocally(chat, localFilter, supportedChats, organization, profile) {
  const byStatus = localFilter.status.length === 0 || localFilter.status.includes(chat.status);
  const byType = localFilter.type === -1 || chat.type === localFilter.type;
  const byBotStopped = localFilter.botStopped === -1 || chat.botStopped === localFilter.botStopped;
  const byTraining = localFilter.training === -1 || chat.training === localFilter.training;
  const myChats =
    !localFilter.myChats ||
    localFilter.myChats === -1 ||
    (supportedChats && supportedChats.map((e) => e.id).includes(chat.id));
  const byDni =
    localFilter.dni === undefined ||
    (chat.client.dni && chat.client.dni.includes && chat.client.dni.includes(localFilter.dni));
  const byPhone =
    localFilter.phone === undefined ||
    chat.client.phoneReverse >= localFilter.phone.split('').reverse().join('');
  const byDate =
    localFilter.date === undefined ||
    (chat.updatedAt.toDate().getTime() >= localFilter.date[0].toDate().getTime() &&
      chat.updatedAt.toDate().getTime() < localFilter.date[1].toDate().getTime());

  const byAssociatedStatus = filterChatsByAssociatedStatus(organization, profile, chat);

  return (
    byStatus &&
    byType &&
    byBotStopped &&
    byTraining &&
    byDni &&
    byPhone &&
    byDate &&
    byAssociatedStatus &&
    myChats
  );
}

const filterChatsByAssociatedStatus = (organization, profile, chat) => {
  if (!organization || !profile || !chat) return true;
  try {
    const { configuration } = organization;
    const chatStatesConfig = configuration?.chatStates?.userAssociatedStates;
    const associatedStates = chatStatesConfig?.associatedStates || [];
    const isRestricted = chatStatesConfig?.enabled;
    const profileHasClaims = profile.claims && profile.claims.length > 0;
    if (!isRestricted || profileHasClaims) return true;
    const associatedState = associatedStates.find(({ state }) => state.key === chat.status);
    return associatedState
      ? associatedState.users.some((user) => user.email === profile.email)
      : true;
  } catch (error) {
    console.log('filterChatsByAssociatedStatus:', error);
    return true;
  }
};

function processChats(chats, localFilter, supportedChats, organization, profile) {
  if (!chats) return;

  const filteredChats = chats.filter((chat) =>
    filterChatsLocally(chat, localFilter, supportedChats, organization, profile),
  );

  return sortChats(filteredChats);
}

function countBotStopped(chats) {
  return filter(chats, (chat) => chat.botStopped === true).length;
}
function countTraining(chats) {
  return filter(chats, (chat) => chat.training === true).length;
}
function countMyChats(chats, supportedChats) {
  return filter(
    chats,
    (chat) => supportedChats && supportedChats.map((e) => e.id).includes(chat.id),
  ).length;
}
function countStatus(chats, statusValue) {
  return filter(chats, (chat) => chat.status === statusValue).length;
}
function countTypes(chats, typeValue) {
  return filter(chats, (chat) => chat.type === typeValue).length;
}

function processCountChats(chats, supportedChats, organization, notificationsService, profile) {
  const states = (
    (organization &&
      organization.states && [...organization.states, 3, notificationsService && 6]) ||
    []
  ).filter((e) => e !== null);
  const filteredAssignedChats = filter(chats, (chat) =>
    filterChatsByAssociatedStatus(organization, profile, chat),
  );
  const types = [typeByName.web, typeByName.whatsapp];

  const byStatus = states.reduce((byStatus, statusValue) => {
    return {
      ...byStatus,
      [statusValue]: countStatus(filteredAssignedChats, statusValue),
    };
  }, {});
  const byType = types.reduce((byType, typeValue) => {
    return {
      ...byType,
      [typeValue]: countTypes(filteredAssignedChats, typeValue),
    };
  }, {});
  const botStopped = countBotStopped(filteredAssignedChats);
  const myChats = countMyChats(filteredAssignedChats, supportedChats);
  const training = countTraining(filteredAssignedChats);

  return { byStatus, byType, botStopped, training, myChats };
}

export default function useChats() {
  const [unsubscribes, setUnsubscribes] = useState([]);

  const firestore = useFirestore();
  const appDispatch = useDispatch();

  const startOfToday = useSelector((state) => state.app.startOfToday);
  const filter = useSelector((state) => state.app.filter);
  const profile = useSelector((state) => state.firebase.profile);
  const showChatsNoEnded = useSelector((state) => state.app.activeChatShowChatsNoEnded);
  const [filterDateLocally, setFilterDateLocally] = useState(false);
  const [filterKeywordLocally, setFilterKeywordLocally] = useState(false); // false | 'phone' | 'dni'
  const organization = useSelector((state) => state.app.organization);
  const mySupportedChats = useSelector((state) => state.app.mySupportedChats);

  const notificationsService =
    organization && organization.services && organization.services.notifications;
  const keyword = filter.query.keyword.trim();

  useEffect(() => {
    if (!profile.organizationId || !showChatsNoEnded) return;

    const usingKeywordFilter = keyword.length >= 6;

    setFilterDateLocally(filter.query.date !== null);
    setFilterKeywordLocally(usingKeywordFilter);
  }, [profile.organizationId, showChatsNoEnded, filter.query.keyword, filter.query.date, keyword]);

  useEffect(() => {
    if (unsubscribes.length > 1) {
      unsubscribes.forEach((uns, index) => {
        if (uns && index !== unsubscribes.length - 1) {
          uns();
        }
      });
      setUnsubscribes([unsubscribes[unsubscribes.length - 1]]);
    }
  }, [unsubscribes]);

  useEffect(() => {
    if (!showChatsNoEnded) {
      appDispatch({
        type: actions.ordered,
        ordered: {
          chats: [],
          ['chatsNoEnded' + statusByName['por hacer']]: [],
          ['chatsNoEnded' + statusByName.alta]: [],
          ['chatsNoEnded' + statusByName['en proceso']]: [],
        },
      });
    }
  }, [appDispatch, showChatsNoEnded]);

  useEffect(() => {
    if (!profile.organizationId || showChatsNoEnded) return;

    const date = filter.query.date;
    const usingKeywordFilter = keyword.length >= 6;
    setFilterKeywordLocally(usingKeywordFilter);

    const usingDateFilter = date !== null && !usingKeywordFilter;
    setFilterDateLocally(date !== null ? usingKeywordFilter : false);

    let unsubscribe;
    if (usingKeywordFilter) {
      if (keywordType(keyword) === 'phone') {
        const phoneReverse = keyword.split('').reverse().join('');

        const promise = firestore
          .collection(collections.chats)
          .where('organizationId', '==', profile.organizationId);

        unsubscribe = promise
          .where('client.phoneReverse', '>=', phoneReverse)
          .where('client.phoneReverse', '<=', phoneReverse + '\uf8ff')
          .onSnapshot((snapshot) => {
            if (showChatsNoEnded) return;
            const _chats = [];
            snapshot.forEach((e) => {
              _chats.push({ id: e.id, ...e.data() });
            });
            const noEnded0 = _chats.filter((e) => e.status === statusByName['por hacer']);
            const noEnded1 = _chats.filter((e) => e.status === statusByName.alta);
            const noEnded2 = _chats.filter((e) => e.status === statusByName['en proceso']);
            appDispatch({
              type: actions.ordered,
              ordered: {
                chats: _chats,
                ['chatsNoEnded' + statusByName['por hacer']]: noEnded0,
                ['chatsNoEnded' + statusByName.alta]: noEnded1,
                ['chatsNoEnded' + statusByName['en proceso']]: noEnded2,
              },
            });
          });
      } else if (keywordType(keyword) === 'dni') {
        const promise = firestore
          .collection(collections.chats)
          .where('organizationId', '==', profile.organizationId)
          .where('client.dni', 'array-contains', keyword);

        unsubscribe = promise.onSnapshot((snapshot) => {
          const _chats = [];
          snapshot.forEach((e) => {
            _chats.push({ id: e.id, ...e.data() });
          });
          const noEnded0 = _chats.filter((e) => e.status === statusByName['por hacer']);
          const noEnded1 = _chats.filter((e) => e.status === statusByName.alta);
          const noEnded2 = _chats.filter((e) => e.status === statusByName['en proceso']);
          appDispatch({
            type: actions.ordered,
            ordered: {
              chats: _chats,
              ['chatsNoEnded' + statusByName['por hacer']]: noEnded0,
              ['chatsNoEnded' + statusByName.alta]: noEnded1,
              ['chatsNoEnded' + statusByName['en proceso']]: noEnded2,
            },
          });
        });
      } else if (keywordType(keyword) === 'session') {
        const promise = firestore
          .collection(collections.chats)
          .where('organizationId', '==', profile.organizationId)
          .where('session', '==', keyword);

        unsubscribe = promise.onSnapshot((snapshot) => {
          const _chats = [];
          snapshot.forEach((e) => {
            _chats.push({ id: e.id, ...e.data() });
          });
          const noEnded0 = _chats.filter((e) => e.status === statusByName['por hacer']);
          const noEnded1 = _chats.filter((e) => e.status === statusByName.alta);
          const noEnded2 = _chats.filter((e) => e.status === statusByName['en proceso']);
          appDispatch({
            type: actions.ordered,
            ordered: {
              chats: _chats,
              ['chatsNoEnded' + statusByName['por hacer']]: noEnded0,
              ['chatsNoEnded' + statusByName.alta]: noEnded1,
              ['chatsNoEnded' + statusByName['en proceso']]: noEnded2,
            },
          });
        });
      } else {
        const promise = firestore.collection(collections.chats).doc(keyword);

        unsubscribe = promise.onSnapshot((snapshot) => {
          const _chats = [{ id: snapshot.id, ...snapshot.data() }];
          const noEnded0 = _chats.filter((e) => e.status === statusByName['por hacer']);
          const noEnded1 = _chats.filter((e) => e.status === statusByName.alta);
          const noEnded2 = _chats.filter((e) => e.status === statusByName['en proceso']);
          appDispatch({
            type: actions.ordered,
            ordered: {
              chats: _chats,
              ['chatsNoEnded' + statusByName['por hacer']]: noEnded0,
              ['chatsNoEnded' + statusByName.alta]: noEnded1,
              ['chatsNoEnded' + statusByName['en proceso']]: noEnded2,
            },
          });
        });
      }
    } else if (usingDateFilter && !usingKeywordFilter) {
      if (filter.local.training === true) {
        unsubscribe = firestore
          .collection(collections.chats)
          .orderBy('updatedAt')
          .where('organizationId', '==', profile.organizationId)
          .where('training', '==', true)
          .onSnapshot((snapshot) => {
            if (!showChatsNoEnded) {
              const _chats = [];
              snapshot.forEach((e) => {
                _chats.push({ id: e.id, ...e.data() });
              });
              const noEnded0 = _chats.filter((e) => e.status === statusByName['por hacer']);
              const noEnded1 = _chats.filter((e) => e.status === statusByName.alta);
              const noEnded2 = _chats.filter((e) => e.status === statusByName['en proceso']);
              appDispatch({
                type: actions.ordered,
                ordered: {
                  chats: _chats,
                  ['chatsNoEnded' + statusByName['por hacer']]: noEnded0,
                  ['chatsNoEnded' + statusByName.alta]: noEnded1,
                  ['chatsNoEnded' + statusByName['en proceso']]: noEnded2,
                },
              });
            }
          });
      } else {
        const [dateStart, dateEnd] = getDayRange(date);
        const promise = firestore
          .collection(collections.chats)
          .orderBy('updatedAt')
          .where('organizationId', '==', profile.organizationId);
        unsubscribe = promise
          .where('updatedAt', '>=', dateStart.toDate())
          .where('updatedAt', '<', dateEnd.toDate())
          .onSnapshot((snapshot) => {
            if (!showChatsNoEnded) {
              const _chats = [];
              snapshot.forEach((e) => {
                _chats.push({ id: e.id, ...e.data() });
              });
              const noEnded0 = _chats.filter((e) => e.status === statusByName['por hacer']);
              const noEnded1 = _chats.filter((e) => e.status === statusByName.alta);
              const noEnded2 = _chats.filter((e) => e.status === statusByName['en proceso']);
              appDispatch({
                type: actions.ordered,
                ordered: {
                  chats: _chats,
                  ['chatsNoEnded' + statusByName['por hacer']]: noEnded0,
                  ['chatsNoEnded' + statusByName.alta]: noEnded1,
                  ['chatsNoEnded' + statusByName['en proceso']]: noEnded2,
                },
              });
            }
          });
      }
    }
    setUnsubscribes((old) => [...old, unsubscribe]);

    return () => {
      unsubscribe && unsubscribe();
    };
  }, [
    startOfToday,
    profile.organizationId,
    filter.query.keyword,
    filter.query.date,
    showChatsNoEnded,
    keyword,
    firestore,
    appDispatch,
    profile.subsidiaryId,
    filter.local.training,
  ]);

  useEffect(() => {
    const [dateStart] = getDayRange(moment());
    if (showChatsNoEnded) {
      const promise = firestore
        .collection(collections.chats)
        .orderBy('updatedAt')
        .where('organizationId', '==', profile.organizationId);

      const unsubscribe = promise
        .where('updatedAt', '<', dateStart.toDate())
        .where('updatedAt', '>', moment().subtract(4, 'days').toDate())
        .onSnapshot((snapshot) => {
          let _chats = [];
          snapshot.forEach((e) => {
            _chats.push({ id: e.id, ...e.data() });
          });

          if (keywordType(keyword) === 'phone') {
            const phoneReverse = keyword.split('').reverse().join('');
            // eslint-disable-next-line no-console
            _chats = _chats.filter(
              (e) =>
                !!(
                  e &&
                  e.client &&
                  e.client.phoneReverse &&
                  e.client.phoneReverse >= phoneReverse &&
                  e.client.phoneReverse <= phoneReverse + '\uf8ff'
                ),
            );
          }

          const noEnded = _chats.filter((e) => e.status !== statusByName.terminado);
          appDispatch({
            type: actions.ordered,
            ordered: {
              chats: _chats,
              chatsNoEnded: noEnded,
            },
          });
          appDispatch({ type: actions.chatsCount, chatsCount: _chats });
        });
      setUnsubscribes((old) => [...old, unsubscribe]);
      return () => {
        unsubscribe && unsubscribe();
      };
    }
  }, [
    appDispatch,
    firestore,
    keyword,
    profile.organizationId,
    profile.subsidiaryId,
    showChatsNoEnded,
  ]);

  const commonChats = useSelector((state) => state.app.ordered.chats);
  const chatsNoEnded = useSelector((state) => state.app.ordered.chatsNoEnded);

  const filterLocal = {
    ...filter.local,
    ...(filterDateLocally && !showChatsNoEnded && !filterKeywordLocally
      ? { date: getDayRange(filter.query.date) }
      : {}),
    ...(filterKeywordLocally ? { [keywordType(keyword)]: keyword } : {}),
  };
  useMySupportedChats(filter);

  const chats = processChats(
    showChatsNoEnded ? chatsNoEnded : commonChats,
    filterLocal,
    mySupportedChats,
    organization,
    profile,
  );

  useEffect(() => {
    let _chats = commonChats;
    if (showChatsNoEnded) {
      _chats = commonChats.filter((e) => e.status !== statusByName.terminado);
    }
    const countChats = processCountChats(
      _chats,
      mySupportedChats,
      organization,
      notificationsService,
      profile,
    );
    appDispatch({ type: actions.countChats, countChats });
  }, [
    appDispatch,
    commonChats,
    notificationsService,
    organization,
    mySupportedChats,
    showChatsNoEnded,
  ]);

  return chats;
}
